import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  Users, 
  FolderKanban, 
  Trophy,
  Brain,
  Loader2,
  ArrowUpRight,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UsageMeters = () => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setLoading(false);
          return;
        }
        const res = await fetch(`${API_URL}/api/entitlements/my-usage`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch usage');
        const data = await res.json();
        setUsage(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUsage();
  }, []);

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !usage) {
    return null; // Silently fail - don't break dashboard
  }

  const { tier, interactions, entities, memory_window_days, billing, upgrade_available } = usage;

  // Format billing cycle end
  const cycleEnd = billing?.cycle_end ? new Date(billing.cycle_end) : null;
  const daysUntilReset = cycleEnd ? Math.max(0, Math.ceil((cycleEnd - new Date()) / (1000 * 60 * 60 * 24))) : null;

  // Check for 80% threshold warnings
  const interactionsNearLimit = interactions?.percent_used >= 80;
  const entitiesNearLimit = Object.values(entities || {}).some(e => 
    e.limit !== 'unlimited' && e.limit > 0 && (e.count / e.limit) >= 0.8
  );
  const showWarning = upgrade_available && (interactionsNearLimit || entitiesNearLimit);

  // Tier colors
  const tierColors = {
    starter: { bg: 'bg-zinc-500/10', text: 'text-zinc-400', border: 'border-zinc-700' },
    core: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
    pro: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30' },
    leadership_plus: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' }
  };

  const currentTierColor = tierColors[tier?.id] || tierColors.starter;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 sm:p-6" data-testid="usage-meters">
      {/* 80% Warning Banner */}
      {showWarning && (
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg" data-testid="usage-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-amber-200 text-sm font-medium">
                You're approaching your {tier?.name || 'plan'} limits
              </p>
              <p className="text-amber-200/70 text-xs mt-0.5">
                {interactionsNearLimit && `${interactions.used}/${interactions.limit} reflections used. `}
                Upgrade for more capacity and extended AI memory.
              </p>
            </div>
            <Link 
              to="/it-leadership#pricing"
              className="text-xs bg-amber-500 text-amber-950 px-3 py-1.5 rounded-lg font-semibold hover:bg-amber-400 transition-colors flex-shrink-0"
            >
              Upgrade
            </Link>
          </div>
        </div>
      )}

      {/* Header with Tier Badge */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${currentTierColor.bg}`}>
            <Zap className={`w-5 h-5 ${currentTierColor.text}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-white font-semibold">{tier?.name || 'Free'}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full ${currentTierColor.bg} ${currentTierColor.text}`}>
                {tier?.id?.toUpperCase() || 'STARTER'}
              </span>
            </div>
            <p className="text-zinc-500 text-xs">{tier?.tagline || 'Basic access'}</p>
          </div>
        </div>
        {upgrade_available && !showWarning && (
          <Link 
            to="/it-leadership#pricing"
            className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
            data-testid="upgrade-link"
          >
            Upgrade
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      {/* Interactions Meter */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-zinc-400 text-sm flex items-center gap-1.5">
            <Brain className="w-4 h-4" />
            Reflections this cycle
          </span>
          <span className={`text-sm font-medium ${interactionsNearLimit ? 'text-amber-400' : 'text-white'}`}>
            {interactions?.used || 0} / {interactions?.limit || 10}
          </span>
        </div>
        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              interactions?.percent_used > 90 ? 'bg-red-500' : 
              interactions?.percent_used >= 80 ? 'bg-amber-500' : 'bg-emerald-500'
            }`}
            style={{ width: `${Math.min(100, interactions?.percent_used || 0)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className={`text-xs ${interactionsNearLimit ? 'text-amber-400' : 'text-zinc-600'}`}>
            {interactions?.remaining || 0} remaining
          </span>
          {daysUntilReset !== null && (
            <span className="text-zinc-600 text-xs">
              Resets in {daysUntilReset} day{daysUntilReset !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Entity Limits Grid */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <EntityMeter 
          icon={<Users className="w-4 h-4" />}
          label="People"
          count={entities?.people?.count || 0}
          limit={entities?.people?.limit}
        />
        <EntityMeter 
          icon={<FolderKanban className="w-4 h-4" />}
          label="Projects"
          count={entities?.projects?.count || 0}
          limit={entities?.projects?.limit}
        />
        <EntityMeter 
          icon={<Trophy className="w-4 h-4" />}
          label="Wins"
          count={entities?.achievements?.count || 0}
          limit={entities?.achievements?.limit}
        />
      </div>

      {/* Memory Window Info */}
      <div className="flex items-center justify-between py-2.5 px-3 bg-zinc-800/50 rounded-lg">
        <span className="text-zinc-500 text-xs">AI memory window</span>
        <span className="text-zinc-300 text-xs font-medium">
          {memory_window_days === 365 ? '1 year' : `${memory_window_days} days`}
        </span>
      </div>
    </div>
  );
};

// Sub-component for entity meters
const EntityMeter = ({ icon, label, count, limit }) => {
  const isUnlimited = limit === 'unlimited' || limit === -1;
  const percent = isUnlimited ? 0 : Math.min(100, (count / limit) * 100);
  const isNearLimit = !isUnlimited && percent >= 80;

  return (
    <div className={`rounded-lg p-2.5 text-center ${isNearLimit ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-zinc-800/50'}`}>
      <div className={`mx-auto mb-1 ${isNearLimit ? 'text-amber-400' : 'text-zinc-500'}`}>
        {icon}
      </div>
      <p className={`text-sm font-semibold ${isNearLimit ? 'text-amber-300' : 'text-white'}`}>{count}</p>
      <p className={`text-xs ${isNearLimit ? 'text-amber-400/70' : 'text-zinc-600'}`}>
        {isUnlimited ? '∞' : `/ ${limit}`} {label}
      </p>
    </div>
  );
};

export default UsageMeters;
